import { useParams } from "@solidjs/router";
import { useAuthWithSSRService } from "@storytell/auth-ssr-implementation";
import { useCallOptions, useChatClient, withBearerToken } from "@storytell/cp-client";
import { useDeviceService } from "@storytell/device";
import { getEnvironmentConfig } from "@storytell/environment";
import { dispatchGlobalEvent, subscribeToGlobalEvent } from "@storytell/events";
import { useLocaleService } from "@storytell/locale";
import { Client as ClientLogger, type Logger, Server as ServerLogger } from "@storytell/logger";
import { createStreamingStore } from "@storytell/streaming";
import { createEffect, createSignal, on, onCleanup, onMount, startTransition } from "solid-js";
import { getRequestEvent, isServer } from "solid-js/web";
import { createAssetsService } from "~/stores/assets";
import { createBillingService } from "~/stores/billing";
import { createChatService } from "~/stores/chat/chatService";
import { createConceptsService } from "~/stores/concepts/conceptsService";
import { createGrantsService } from "~/stores/grants/grantsService";
import { createGuidesService } from "~/stores/guides/guidesService";
import { createKnowledgeUIService } from "~/stores/knowledge/knowledgeUIService";
import { createLabelsService } from "~/stores/labels";
import { createProjectsService } from "~/stores/projects";
import { createRelsService } from "~/stores/rels/relsService";
import { createUIService } from "~/stores/uiService";
import { createUserContextService } from "~/stores/userContext";
import { createViewsService } from "~/stores/views";
import type { Wire } from "./definition";
import { ChatTitleService } from "./services/chatTitleService";

export const newWire = (): Wire => {
  const logger = initLogger();
  const locale = useLocaleService();
  const device = useDeviceService({
    logger,
    variant: "app",
  });
  const event = getRequestEvent();

  const streamingService = createStreamingStore();

  const userAgent = () => {
    if (isServer) {
      return event?.request?.headers.get("user-agent") || "";
    }
    return navigator.userAgent;
  };

  const auth = useAuthWithSSRService({
    logger,
    deviceId: device.getDeviceId,
    locale: locale.getLocale,
    userAgent,
  });

  const knowledgeUi = createKnowledgeUIService();

  // TODO: @andi Better way to check if the guest user has started any chats
  const [hasChats, setHasChats] = createSignal(false);
  const fetchHasChats = () => {
    if (!auth.isReady() || !auth.isGuest() || !auth.userId()) return;
    const client = useChatClient();
    client
      .listRecentChats(
        {
          pageSize: 1,
        },
        useCallOptions(withBearerToken(auth.token())),
      )
      .then((res) => {
        setHasChats(res.isOk() ? Number(res.value.totalCount) > 0 : false);
      });
  };
  createEffect(
    on(
      () => auth.isReady() && auth.isGuest(),
      () => {
        fetchHasChats();
      },
    ),
  );
  onMount(() => {
    const unsubscribe = subscribeToGlobalEvent("prompt-submitted-successfuly", () => {
      fetchHasChats();
    });
    onCleanup(unsubscribe);
  });

  const billing = createBillingService({
    authReady: auth.isReady,
    authToken: auth.token,
    authUserId: auth.userId,
    authRegistered: () => auth.isRegistered(),
    hasChats,
    userId: auth.userId,
  });
  const ui = createUIService();

  const params = useParams();
  const chatTitle = new ChatTitleService();

  const projects = createProjectsService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    userId: auth.userId,
  });
  const userContext = createUserContextService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    userId: auth.userId,
  });

  let assetsOrConceptsSynced = () => {};
  const assets = createAssetsService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    userId: auth.userId,
    assetsSynced: () => assetsOrConceptsSynced(),
  });

  const chat = createChatService({
    urlParams: () => params,
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    projectId: () => userContext.get()?.projectId,
    userId: auth.userId,
    stream: streamingService,
    assets,
    reloadBillingInfo: () => {
      startTransition(() => {
        billing.refetch();
      });
    },
    generateChatTitle: async (chatId, message, model) => {
      try {
        const title = await chatTitle.generateTitle(chatId, message, model);
        // TODO(amalucelli): Dispatch event to update sidebar when GlobalEvents supports chat-title-generated
        dispatchGlobalEvent("chat-title-generated", {
          chatId,
          title,
        });
      } catch (error) {
        console.error("Failed to generate chat title:", error);
      }
    },
    setChatTitle: (chatId, title) => {
      chatTitle.setChatTitle(chatId, title);
    },
  });

  const guides = createGuidesService({
    authService: auth,
    chatService: chat,
  });
  const views = createViewsService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    userId: auth.userId,
  });

  const concepts = createConceptsService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    userId: auth.userId,
    conceptsSynced: () => assetsOrConceptsSynced(),
  });
  const labels = createLabelsService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    userId: auth.userId,
  });
  const rels = createRelsService({ assets, concepts });

  // Have to define this later so we can access the assets and concepts services
  assetsOrConceptsSynced = () => {
    rels.rebuildFullCache({ assets: assets.all(), concepts: concepts.all() });
  };

  const grants = createGrantsService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    userId: auth.userId,
  });

  return {
    auth,
    grants,
    stream: streamingService,
    chat,
    chatTitle,
    billing,
    ui,
    projects,
    userContext,
    labels,
    views,
    assets,
    concepts,
    rels,
    knowledgeUi,
    device,
    guides,
    logger,
    locale,
    environment: getEnvironmentConfig(),
  };
};

export const validateWire = (wire: Wire) => {
  const newErrorMessage = (dependency: string): string => {
    return `Dependency validation for the wire failed for '${dependency}'.`;
  };
  if (!wire.logger) {
    throw new Error(newErrorMessage("logger"));
  }
  if (!wire.auth) {
    throw new Error(newErrorMessage("auth"));
  }
  if (!wire.environment || Object.keys(wire.environment).length === 0) {
    throw new Error(newErrorMessage("environment"));
  }
  return;
};

// initLogger initializes the logger based on the environment.
const initLogger = (): Logger => (isServer ? new ServerLogger("_") : new ClientLogger("_"));
